import React from 'react';
import { useImportJobs } from './ImportJobContext';

/**
 * The running-imports indicator in the sidebar.
 *
 * This is what makes "you can navigate away" true rather than a claim: the
 * import lives on the server, and this reads it back, so leaving the importer
 * page no longer means losing sight of the job.
 */
const ImportTicker: React.FC = () => {
  const { activeImports, finishedImports } = useImportJobs();

  // Nothing running, but something finished a moment ago: say so rather than
  // simply disappearing. A vanished indicator is indistinguishable from one
  // that never loaded, so success was the one outcome with no signal at all.
  if (activeImports.length === 0 && finishedImports.length > 0) {
    const names = Array.from(
      new Set(finishedImports.map((job) => job.dataset_label)),
    ).join(', ');
    return (
      <div className="import-ticker is-done" role="status" aria-live="polite">
        <span className="import-ticker-tick" aria-hidden="true">
          ✓
        </span>
        <span className="import-ticker-text">
          {finishedImports.length === 1
            ? `${names} import finished`
            : `${finishedImports.length} imports finished: ${names}`}
        </span>
      </div>
    );
  }

  if (activeImports.length === 0) return null;

  const stalledJobs = activeImports.filter((job) => job.stalled);
  const summary = activeImports
    .map((job) => {
      const pct =
        job.row_count > 0
          ? Math.min(100, Math.round((job.processed / job.row_count) * 100))
          : null;
      // "Stopped" rather than a frozen percentage: a number that never moves
      // reads as slow progress, which is the wrong thing to wait for.
      const state = job.stalled
        ? ' stopped'
        : pct !== null
          ? ` ${pct}%`
          : '';
      return `${job.dataset_label}${state}${
        job.is_mine ? '' : ` (${job.started_by || 'a colleague'})`
      }`;
    })
    .join(' · ');

  return (
    <div
      className={`import-ticker${stalledJobs.length ? ' is-stalled' : ''}`}
      role="status"
      aria-live="polite"
      title={activeImports
        .map((j) => `${j.dataset_label}: ${j.file_name || 'import'} — ${j.status}`)
        .join('\n')}
    >
      <span className="import-ticker-dot" aria-hidden="true" />
      <span className="import-ticker-text">
        {stalledJobs.length === activeImports.length
          ? `Import stopped: ${summary}`
          : activeImports.length === 1
            ? `Importing: ${summary}`
            : `${activeImports.length} imports running: ${summary}`}
      </span>
    </div>
  );
};

export default ImportTicker;
